import React, { useState, useEffect, useCallback, useRef } from "react";
import { useQuery, useMutation } from '@apollo/client';
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from 'react-redux';
import { setpublisherGlobalData } from '../../redux/currentpublisher/publisherGlobalDataSlice.js';
import { baseProductObject } from './BaseProductObject.js';
import ProductForm from "./ProductForm.js";
import Breadcrumbs from "../../components/layout/Breadcrumbs.js";
import SaveButtons from "../../components/layout/SaveButtons.js";
import LeavePagePrompt from "../../components/LeavePagePrompt.js";
import QUERY_PRODUCT from "../../graphql/queries/Products/QueryProduct.js";
import QUERY_PRODUCT_WORK_COUNTS from "../../graphql/queries/ProductWork/QueryProductWorkCounts.js";
import UPDATE_PRODUCT_MUTATION from "../../graphql/mutations/Products/UpdateProduct.js";
import CREATE_PRODUCT_CREATOR_MUTATION from "../../graphql/mutations/Products/CreateProductCreator.js";
import DELETE_PRODUCT_CREATOR_MUTATION from "../../graphql/mutations/Products/DeleteProductCreator.js";
import DELETE_PRODUCT_MUTATION from "../../graphql/mutations/Products/DeleteProduct.js";
import CREATE_PRODUCT_MUTATION from "../../graphql/mutations/Products/CreateProduct.js";
import UPDATE_PRODUCT_WORK_MUTATION from "../../graphql/mutations/ProductWork/UpdateProductWork.js";
import CREATE_IMPRINT_MUTATION from "../../graphql/mutations/PublisherData/CreateImprint.js";
import CREATE_SERIES_MUTATION from "../../graphql/mutations/PublisherData/CreateSeries.js";

import {
  Pane,
  Paragraph,
  Badge,
  toaster,
  EmptyState,
  InfoSignIcon,
  Card,
  LockIcon,
  Tooltip,
  Button,
  Dialog,
  TextInputField,
  Heading,
  Spinner,
  Text,
  Strong,
  PlusIcon,
  Link as EvergreenLink,
  minorScale,
  majorScale,
  SelectField
} from 'evergreen-ui';
import { makeDateString, formatCurrency } from "../../util.js";
import DatePicker from "react-date-picker";
import { ForeignKeyDropDownInput } from '../../inputs';
import { TextInputFieldInput, TextareaFieldInput } from '../../inputs';

// Component for displaying a single format in the middle column
function FormatListItem({ product, isSelected, onSelect }) {
  return (
    <Card
      key={product.id}
      elevation={1}
      padding={majorScale(1)}
      marginBottom={majorScale(1)}
      cursor="pointer"
      onClick={() => onSelect(product)}
      border={isSelected ? "2px solid var(--darkgreen)" : "default"}
      backgroundColor={isSelected ? "var(--lightgreen)" : "white"}
      hoverElevation={2}
    >
      <Heading size={400} marginBottom={minorScale(1)}>
        {product.format?.description || 'Unknown Format'}
      </Heading>
      <Text size={300} display="block">ISBN/UID: {product.isbn || 'N/A'}</Text>
      <Text size={300} display="block">
        Price: {formatCurrency(product.retailPrice || 0)}
      </Text>
      <Text size={300} display="block">
        Inventory: {product.inventoryQuantity ?? 'N/A'}
      </Text>
    </Card>
  );
}

// Helper function to get format reference from publisherGlobalData
function getFormatReference(formatId, publisherGlobalData, fallbackFormat = null) {
  if (!formatId) {
    return null;
  }

  if (!publisherGlobalData?.onixproductforms) {
    return fallbackFormat;
  }

  // Ensure consistent string comparison
  const formatIdStr = String(formatId);

  // First, try to find an exact reference equality match (same object)
  if (fallbackFormat && publisherGlobalData.onixproductforms.includes(fallbackFormat)) {
    return fallbackFormat;
  }

  // Otherwise find by ID
  const reference = publisherGlobalData.onixproductforms.find(
    f => String(f.id) === formatIdStr
  );

  if (reference) {
    return reference;
  } else {
    // If we can't find a matching format, but have a fallback format with the same ID,
    // return the fallback to maintain integrity
    if (fallbackFormat && String(fallbackFormat.id) === formatIdStr) {
      return fallbackFormat;
    }
    return fallbackFormat;
  }
}

function Product() {
  const userid = useSelector((state) => state.user.value);
  const currentpublisher = useSelector((state) => state.currentpublisher.value);
  const publisherGlobalData = useSelector((state) => state.publisherGlobalData.value);
  const dispatch = useDispatch();

  const [product, setProduct] = useState(null);
  const [productWork, setProductWork] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productCounts, setProductCounts] = useState({
    ordersCount: 0,
    expensesCount: 0,
    royaltiesCount: 0
  });

  const [changed, setChanged] = useState([]);
  const [staged, setStaged] = useState(baseProductObject);
  const [stagedCreators, setStagedCreators] = useState([]);
  const [deletedCreators, setDeletedCreators] = useState([]);
  const [currentCreators, setCurrentCreators] = useState([]);
  const [editing, setEditing] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [showAddFormat, setShowAddFormat] = useState(false);

  const isNavigationRef = useRef(false);
  const { productId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const handleUpdateStaged = useCallback((item) => {
    setStaged(prevStaged => {
      let newStaged = { ...prevStaged };
      
      // Handle the case where key contains 'productWork.' prefix
      if (item.key && item.key.startsWith('productWork.')) {
        const actualKey = item.key.replace('productWork.', '');
        newStaged.productWork = { ...prevStaged.productWork, [actualKey]: item.val };
      } else if (item.el === 'productWork') {
        newStaged.productWork = { ...prevStaged.productWork, [item.key]: item.val };
      } else {
        newStaged = { ...prevStaged, [item.key]: item.val };
      }
      return newStaged;
    });

    setChanged(prevChanged => {
      // Determine the key to use for tracking changes
      let keyToUpdate;
      if (item.key && item.key.startsWith('productWork.')) {
        keyToUpdate = item.key; // Already has productWork. prefix
      } else if (item.el === 'productWork') {
        keyToUpdate = `productWork.${item.key}`;
      } else {
        keyToUpdate = item.key;
      }
      
      const existingIndex = prevChanged.findIndex(c => c.key === keyToUpdate);
      
      if (existingIndex > -1) {
        // Update existing change with new value
        const updatedChanged = [...prevChanged];
        updatedChanged[existingIndex] = { key: keyToUpdate, val: item.val };
        return updatedChanged;
      } else {
        // Add new change with actual value
        return [...prevChanged, { key: keyToUpdate, val: item.val }];
      }
    });
    if (!editing) {
      setEditing(true);
    }
  }, [editing]);

  const { loading, error, data, refetch: _refetch } = useQuery(QUERY_PRODUCT, {
    variables: {
      publisher: String(currentpublisher.id),
      id: parseInt(productId)
    },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    onCompleted: (queryData) => {
      const fetchedProduct = queryData?.product?.[0] || null;

      // --- Directly set core states here ---
      setProduct(fetchedProduct);
      if (fetchedProduct) {
        const work = fetchedProduct.productWork || null;
        setProductWork(work);
        setSelectedProduct(fetchedProduct);
        // Also set related products and creators immediately based on the fetched data
        const related = work?.products || [];
        const creators = fetchedProduct.productcreatorSet || [];
        // Update deletable check to include expenses
        const deletable = (!fetchedProduct.orderproductSet || fetchedProduct.orderproductSet.length === 0) &&
                          (!fetchedProduct.expenseSet || fetchedProduct.expenseSet.length === 0);
        setRelatedProducts(related);
        setCurrentCreators(creators);
        setCanDelete(deletable);

        // --- Force state synchronization by calling handleSelectProduct ---
        handleSelectProduct(fetchedProduct, work);
        // ------------------------------------------------------------------

      } else {
          setProductWork(null);
          setSelectedProduct(null);
          setRelatedProducts([]);
          setCurrentCreators([]);
          setCanDelete(false);
          // Also clear staged state if no product is found
          setStaged(baseProductObject);
      }
      // --- End core state setting ---

    },
    onError: (queryError) => {
      toaster.danger(`Error loading product data: ${queryError.message}`);
      if (queryError.networkError) {
      }
      if (queryError.graphQLErrors) {
        queryError.graphQLErrors.forEach((err, index) => {
        });
      }
      setProduct(null);
      setProductWork(null);
      setRelatedProducts([]);
      setSelectedProduct(null);
      setStaged(baseProductObject);
      setCurrentCreators([]);
      setCanDelete(false);
    }
  });

  // NEW useEffect for location changes to handle navigation scenarios
  useEffect(() => {
    // This effect will run when location changes (i.e., during navigation)
    isNavigationRef.current = true;

    // The format correction will happen in our other useEffects
  }, [location.key]); // location.key changes on navigation

  // NEW useEffect to initialize STAGED state and reset editing flags
  // This runs AFTER onCompleted sets the core product states, or when globalData changes.
  useEffect(() => {
    // Ensure both product and the format list are available before proceeding
    if (product && publisherGlobalData?.onixproductforms) {
        // Now that product, productWork, selectedProduct etc are set by onCompleted,
        // focus this effect on constructing the STAGED object correctly.
        const work = product.productWork || null; // Use the state variable productWork which should be up-to-date

        // Deep copy product and work to avoid modifying cached data
        // Check if product or work is null before stringifying
        const productCopy = product ? JSON.parse(JSON.stringify(product)) : null;
        const workCopy = work ? JSON.parse(JSON.stringify(work)) : null;

        // Ensure productCopy is not null before accessing format
        if (!productCopy) {
          return; // Exit if product data is unexpectedly null
        }

        // Use helper function to get format reference from global data
        // Pass isNavigation flag to ensure correct handling
        const formatReference = getFormatReference(
          productCopy.format?.id,
          publisherGlobalData,
          productCopy.format // Pass the potentially non-referenced format as fallback
        );

        // Construct the initial staged state
        const initialStaged = {
            ...productCopy,
            format: formatReference, // Use reference from helper function
            productWork: workCopy
        };
        initialStaged.id = product.id;

        setStaged(initialStaged);

        // Reset navigation flag after successful initialization
        if (isNavigationRef.current) {
          isNavigationRef.current = false;
        }
    }
  }, [product, publisherGlobalData?.onixproductforms, location.key]); // Keep location.key to trigger on navigation

  console.log('[Product.js] Component Render - Query State:', { loading, error: !!error, data: !!data });
  console.log('[Product.js] Component Render - Core State Check:', { product: !!product, productWork: !!productWork, selectedProduct: !!selectedProduct });

  // Query for product work counts
  const { data: countData, refetch: refetchCounts } = useQuery(QUERY_PRODUCT_WORK_COUNTS, {
    variables: {
      productWorkId: productWork?.id
    },
    skip: !productWork?.id,
    onCompleted: (data) => {
      if (data?.productWorkCounts) {
        // Field names already match our state
        setProductCounts(data.productWorkCounts);
      }
    }
  });

  const refetch = useCallback(() => {
      _refetch();
      if (productWork?.id) {
        refetchCounts();
      }
  }, [_refetch, refetchCounts, productWork?.id]);

  const [updateProduct, { loading: updateLoading }] = useMutation(UPDATE_PRODUCT_MUTATION);
  const [createProductCreator] = useMutation(CREATE_PRODUCT_CREATOR_MUTATION);
  const [deleteProductCreator] = useMutation(DELETE_PRODUCT_CREATOR_MUTATION);
  const [deleteProduct, { loading: deleteLoading }] = useMutation(DELETE_PRODUCT_MUTATION);
  const [updateProductWork, { loading: updateWorkLoading }] = useMutation(UPDATE_PRODUCT_WORK_MUTATION);
  const [createImprint] = useMutation(CREATE_IMPRINT_MUTATION);
  const [createSeries] = useMutation(CREATE_SERIES_MUTATION);

  const resetForm = () => {
    if (!selectedProduct) {
      setStaged(baseProductObject);
      setChanged([]);
      setDeletedCreators([]);
      setStagedCreators([]);
      return;
    }

    // Make deep copies to avoid mutation
    const productToStage = JSON.parse(JSON.stringify(selectedProduct));
    const workCopy = productWork ? JSON.parse(JSON.stringify(productWork)) : null;

    // Use helper function to get format reference from global data
    const formatReference = getFormatReference(
      selectedProduct.format?.id,
      publisherGlobalData,
      productToStage.format
    );

    // Construct the reset staged state
    const resetStaged = {
        ...productToStage, // Details of the currently selected format
        format: formatReference, // Use reference from global data
        productWork: workCopy // Ensure main productWork is used
    };

    // Ensure ID matches selected product
    resetStaged.id = selectedProduct.id;

    setStaged(resetStaged); // Use the reconstructed state
    setChanged([]);
    setDeletedCreators([]);
    setStagedCreators([]);
    setCurrentCreators(selectedProduct?.productcreatorSet || []);
  };

  const handleSave = async () => {
    const workRelatedKeys = ['title', 'description', 'imprint', 'series'];
    const formatRelatedKeys = Object.keys(staged).filter(k => !workRelatedKeys.includes(k) && k !== 'productWork' && k !== 'id' && k !== '__typename' && k !== 'productcreatorSet' && k !== 'orderproductSet');

    const workChanges = changed.filter(c => {
      // Check for both direct keys and productWork.key format
      if (workRelatedKeys.includes(c.key)) return true;
      if (c.key.startsWith('productWork.')) {
        const fieldName = c.key.replace('productWork.', '');
        return workRelatedKeys.includes(fieldName);
      }
      return false;
    });
    const formatChanges = changed.filter(c => formatRelatedKeys.includes(c.key) || c.key === 'formatId');

    if (!editing || (workChanges.length === 0 && formatChanges.length === 0 && stagedCreators.length === 0 && deletedCreators.length === 0)) {
      setEditing(false);
      return;
    }

    let saveSuccess = true;
    let errorMessages = [];

    try {
      // Update ProductWork if there are work-related changes
      if (workChanges.length > 0 && productWork?.id) {
        const workInput = workChanges.reduce((acc, curr) => {
          // Remove productWork. prefix if present
          const key = curr.key.startsWith('productWork.') 
            ? curr.key.replace('productWork.', '') 
            : curr.key;
          
          if (key === 'imprint') {
            acc['imprintId'] = curr.val?.id ? parseInt(curr.val.id) : null; // Use camelCase imprintId
          } else if (key === 'series') {
            acc['seriesId'] = curr.val?.id ? parseInt(curr.val.id) : null; // Use camelCase seriesId
          } else {
            // Pass direct value for title/description
            acc[key] = curr.val;
          }
          return acc;
        }, {});

        // Add the required publisherId (camelCase) to the input object
        workInput.publisherId = currentpublisher?.id ? String(currentpublisher.id) : null;

        // Ensure all fields expected by ProductWorkInput are present, even if null
        workInput.title = workInput.title ?? productWork.title; // Use original if not changed
        workInput.imprintId = workInput.imprintId ?? (productWork.imprint?.id ? parseInt(productWork.imprint.id) : null);
        workInput.seriesId = workInput.seriesId ?? (productWork.series?.id ? parseInt(productWork.series.id) : null);
        workInput.description = workInput.description ?? productWork.description;

        const workVariables = {
          id: parseInt(productWork.id),
          input: workInput // Nest the changes inside the 'input' object
        };

        await updateProductWork({ variables: workVariables });
        toaster.success("Work details updated.", { duration: 3 });
      } else if (workChanges.length > 0 && !productWork?.id) {
        errorMessages.push("Could not save work details (missing ID).");
        saveSuccess = false;
      }

      // Update Product (format) if there are format-related changes
      if (formatChanges.length > 0 && selectedProduct?.id) {
        // Helper to format dimension values as JSON objects
        const formatDimensionJson = (value, defaultUnit = '') => {
          if (value === '' || value === null || value === undefined) return null;
          if (typeof value === 'object' && value.value !== undefined) {
            // Already in the right format, stringify it
            const dimObj = {
              value: value.value,
              unit: value.unit || defaultUnit
            };
            return JSON.stringify(dimObj);
          }
          // Direct value
          const dimObj = { value: value, unit: defaultUnit };
          return JSON.stringify(dimObj);
        };

        // Correctly construct variables for UpdateProduct mutation
        const formatUpdateVars = formatChanges.reduce((acc, curr) => {
            // Map frontend state keys to backend mutation argument names (now using camelCase)
            if (curr.key === 'format' || curr.key === 'formatId') {
              acc['formatId'] = curr.val?.id ? String(curr.val.id) : null; // Use formatId (camelCase), send as String (GraphQL ID accepts String)
            } else if (curr.key === 'status') {
              acc['status'] = curr.val?.id ? parseInt(curr.val.id, 10) : null; // Try sending status ID as Integer
            } else if (curr.key === 'inventoryQuantity'){
              acc['inventory'] = curr.val ? parseInt(curr.val) : 0; // Map inventoryQuantity to inventory, ensure Int
            } else if (curr.key === 'retailPrice') {
              acc[curr.key] = curr.val ? parseFloat(curr.val) : 0; // Ensure Float
            } else if (curr.key === 'weight' || curr.key === 'width' || curr.key === 'height' || curr.key === 'thickness') {
              // Extract numeric value and parse as Float, send null if invalid/empty
              const numericValue = parseFloat(typeof curr.val === 'object' && curr.val !== null ? curr.val?.value : curr.val);
              acc[curr.key] = !isNaN(numericValue) ? numericValue : null;
            } else if (curr.key === 'cartonQty' || curr.key === 'pageCount' || curr.key === 'volume' || curr.key === 'edition') {
              acc[curr.key] = curr.val ? parseInt(curr.val, 10) : null; // Ensure Int, use null if parsing fails or value is empty
            } else if (curr.key === 'audience') {
              // Audience is a foreign key like status
              acc['audience'] = curr.val?.id ? String(curr.val.id) : null;
            } else if (curr.key === 'bisac') {
              // BISAC is an array that needs to be JSON stringified
              acc['bisac'] = Array.isArray(curr.val) ? JSON.stringify(curr.val) : curr.val;
            } else {
              // Directly map other keys (subtitle, isbn, shipDate, publicationDate, adminNotes, audienceDetails, keySellingPoints, marketing)
              // The mutation expects camelCase, not snake_case
              acc[curr.key] = curr.val;
            }
            return acc;
          }, {});

        // Add the ID separately, ensuring it's a string to match GraphQL ID!
        formatUpdateVars.id = String(selectedProduct.id);

        console.log("Submitting format update with variables:", formatUpdateVars);

        // Submit the updateProduct mutation with the CORRECT variables
        let updateResult = await updateProduct({
          variables: {
              id: String(selectedProduct.id), // Send ID as String
              ...formatUpdateVars // Spread the collected format fields
          }
        });
        toaster.success("Format details updated.", { duration: 3 });
      } else if (formatChanges.length > 0 && !selectedProduct?.id) {
        errorMessages.push("Could not save format details (missing ID).");
        saveSuccess = false;
      }

      if (stagedCreators.length > 0) {
        await Promise.all(stagedCreators.map(async (creator) => {
          // Log the creator object being processed and the variables for the mutation
          const variablesToSend = {
            product: String(selectedProduct.id),
            creator: String(creator.creator.id),
            onixContributorCode: String(creator.onixContributorCode.id)
          };
          console.log('[Product.js] handleSave - Calling createProductCreator with variables:', variablesToSend);
          console.log('[Product.js] handleSave - Original creator object from stagedCreators:', creator);

          await createProductCreator({
            variables: variablesToSend
          });
        }));
        toaster.success("Creators added.", { duration: 3 });
      }

      if (deletedCreators.length > 0) {
        await Promise.all(deletedCreators.map(async (creatorLinkId) => {
          await deleteProductCreator({ variables: { id: parseInt(creatorLinkId) } });
        }));
        toaster.success("Creators removed.", { duration: 3 });
      }

      if (saveSuccess) {
        setChanged([]);
        setDeletedCreators([]);
        setStagedCreators([]);
        setEditing(false);
        toaster.closeAll();
        toaster.success("Changes saved successfully!", { duration: 5 });
        console.log('[Product.js] handleSave - All changes processed. Refetching data.');
        refetch();
      } else {
        toaster.danger(`Some changes failed to save: ${errorMessages.join('; ')}`, { duration: 10 });
      }

    } catch (err) {
      saveSuccess = false;
      toaster.danger(`Error saving changes: ${err.message}`, { duration: 10 });
      if (err.graphQLErrors) {
        err.graphQLErrors.forEach((graphQLError, index) => {
        });
      }
      if (err.networkError) {
      }
    }
  };

  const handleDelete = async () => {
    if (!selectedProduct || !canDelete) {
      return;
    }

    try {
      await deleteProduct({ variables: { id: parseInt(selectedProduct.id) } });
      toaster.success(`Product format ${selectedProduct.isbn || selectedProduct.id} deleted successfully!`);
      if (relatedProducts.length <= 1) {
        navigate('/products');
      } else {
        refetch();
        navigate('/products');
      }
    } catch (err) {
      toaster.danger(`Error deleting product: ${err.message}`);
      if (err.graphQLErrors) {
        err.graphQLErrors.forEach((graphQLError, index) => {
        });
      }
      if (err.networkError) {
      }
    }
  };

  const handleSelectProduct = (productToSelect, directWorkData = null) => {
    if (editing && changed.length > 0) {
      if (!window.confirm("You have unsaved changes. Are you sure you want to switch formats and discard them?")) {
        return;
      }
    }
    setSelectedProduct(productToSelect);

    // IMPROVED: Ensure format reference comes from publisherGlobalData
    if (!productToSelect) {
      setStaged(baseProductObject);
      setCurrentCreators([]);
      setCanDelete(false);
      setEditing(false);
      setChanged([]);
      setDeletedCreators([]);
      setStagedCreators([]);
      return;
    }

    // Make a deep copy of the product to avoid mutation issues
    const productCopy = JSON.parse(JSON.stringify(productToSelect));

    // Use helper function to get format reference from global data
    const formatReference = getFormatReference(
      productToSelect.format?.id,
      publisherGlobalData,
      productCopy.format
    );

    // Use the directly passed work data if available, otherwise use state
    const workToUse = directWorkData || productWork;
    const workCopy = workToUse ? JSON.parse(JSON.stringify(workToUse)) : null;

    // Set a flag to ensure proper format update during navigation (still useful?)
    isNavigationRef.current = true;

    // Construct the new staged state
    const newStaged = {
      ...productCopy, // Start with selected format details (deep copy)
      format: formatReference, // Use the reference from global data
      productWork: workCopy // ALWAYS use the determined productWork state (deep copy)
    };

    // Ensure the ID in the staged object matches the selected product
    newStaged.id = productToSelect.id;

    setStaged(newStaged); // Use this carefully constructed state
    setCurrentCreators(productToSelect?.productcreatorSet || []);
    setCanDelete(!productToSelect?.orderproductSet || productToSelect?.orderproductSet.length === 0);
    setEditing(false); // Reset editing state when changing product
    setChanged([]);
    setDeletedCreators([]);
    setStagedCreators([]);
  };

  const handleAttachOrUpdateCreator = (id, creator, code) => {
    if (!creator || !code) {
      return;
    }

    // Create a well-formed creator link object
    const newCreatorLink = {
      id: id || `new-${Date.now()}-${Math.random()}`,
      creator: creator,
      onixContributorCode: code,
      __typename: "ProductCreatorType"
    };

    // First update staged creators (for new links)
    if (!id && !stagedCreators.some(c => c.creator.id === creator.id && c.onixContributorCode.id === code.id)) {
      setStagedCreators(prev => [...prev, newCreatorLink]);
    }

    // Then update the current creators display
    setCurrentCreators(prev => {
      // Check if we're updating an existing creator link
      const existingIndex = prev.findIndex(c => c.id === id);

      if (existingIndex > -1) {
        // Updating an existing link
        const updated = [...prev];
        updated[existingIndex] = newCreatorLink;
        return updated;
      } else if (!prev.some(c => c.creator.id === creator.id && c.onixContributorCode.id === code.id)) {
        return [...prev, newCreatorLink];
      }

      // Link already exists, no change needed
      return prev;
    });

    // Mark creators as changed for save tracking
    setChanged(prev => {
      // Only add if not already in the changed list
      if (!prev.some(c => c.key === 'creators')) {
        return [...prev, { key: 'creators', val: 'modified' }];
      }
      return prev;
    });
  };

  const handleRemoveCreator = creatorLinkId => {
    if (!creatorLinkId) {
      return;
    }

    // Find the creator link to remove
    const linkToRemove = currentCreators.find(c => c.id === creatorLinkId);
    if (!linkToRemove) {
      return;
    }

    console.log('[Product.js] Removing creator link:', linkToRemove);

    // If it's a persisted creator (has numeric ID), add to deletedCreators for backend deletion
    if (typeof creatorLinkId === 'number') {
      setDeletedCreators(prev => [...new Set([...prev, creatorLinkId])]);
    }

    // Remove from stagedCreators if it exists there
    setStagedCreators(prev => {
      const filtered = prev.filter(c => c.id !== creatorLinkId);
      return filtered;
    });

    // Remove from currentCreators for UI update
    setCurrentCreators(prev => {
      const filtered = prev.filter(c => c.id !== creatorLinkId);
      return filtered;
    });

    // Mark creators as changed for save tracking
    setChanged(prev => {
      // Only add if not already in the changed list
      if (!prev.some(c => c.key === 'creators')) {
        return [...prev, { key: 'creators', val: 'modified' }];
      }
      return prev;
    });

    // Success feedback to user
    toaster.notify(`Removed ${linkToRemove.creator.displayName} (${linkToRemove.onixContributorCode.description})`);
  };

  const handleRemoveCreatorStaged = (creatorLinkId) => {
    setDeletedCreators(prev => [...prev, creatorLinkId]);
    setCurrentCreators(prev => prev.filter(pc => pc.id !== creatorLinkId));
    if (!editing) {
      setEditing(true);
    }
  };

  // Helper to safely parse interior images string
  const parseInteriorImagesString = (imagesString) => {
    if (!imagesString || typeof imagesString !== 'string' || imagesString.trim() === "") {
      return [];
    }
    try {
      let parsed = JSON.parse(imagesString);
      // Handle potential double-stringification (e.g., "\"[]\"" or "\"['path1']\"")
      if (typeof parsed === 'string') {
        parsed = JSON.parse(parsed);
      }
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.error("Error parsing interiorImages string:", e, "Value was:", imagesString);
      return []; // Return empty array on error
    }
  };

  const handleImageUpload = useCallback((imageType, imageUrl) => {
    let storagePath = imageUrl;

    if (imageUrl && imageUrl.includes('firebasestorage.googleapis.com')) {
      try {
        const fullUrl = new URL(imageUrl);
        storagePath = decodeURIComponent(fullUrl.pathname.split('/o/')[1]);
      } catch (e) {
        console.error("Error parsing Firebase URL in handleImageUpload:", e);
        toaster.danger(`Error processing image URL. Please try again. ${e.message}`);
        return; // Exit if URL parsing fails
      }
    }

    setStaged(prevStaged => {
      let newStagedValues = {};
      let valueForChangedArray;

      if (imageType === 'coverImage') {
        newStagedValues.coverImage = storagePath;
        valueForChangedArray = storagePath;
      } else if (imageType === 'interiorImages') {
        let currentImages = parseInteriorImagesString(prevStaged.interiorImages);
        // Ensure we don't add duplicates, though less likely with unique firebase paths
        if (!currentImages.includes(storagePath)) {
          currentImages.push(storagePath);
        }
        const stringifiedImages = JSON.stringify(currentImages);
        newStagedValues.interiorImages = stringifiedImages;
        valueForChangedArray = stringifiedImages;
      } else {
        return prevStaged;
      }

      setChanged(prevChanged => {
        const existingChangeIndex = prevChanged.findIndex(c => c.key === imageType);
        if (existingChangeIndex > -1) {
          const updatedChanged = [...prevChanged];
          updatedChanged[existingChangeIndex] = { key: imageType, val: valueForChangedArray };
          return updatedChanged;
        } else {
          return [...prevChanged, { key: imageType, val: valueForChangedArray }];
        }
      });

      return { ...prevStaged, ...newStagedValues };
    });

    if (!editing) {
      setEditing(true);
    }
  }, [editing]);

  const handleRemoveInteriorImage = useCallback((imagePathToRemove) => {
    setStaged(prevStaged => {
      let currentImages = parseInteriorImagesString(prevStaged.interiorImages);

      const updatedImagesArray = currentImages.filter(path => path !== imagePathToRemove);
      const finalUpdatedImagesString = JSON.stringify(updatedImagesArray);

      // Update 'changed' array
      setChanged(prevChanged => {
        const imageType = 'interiorImages';
        const existingChangeIndex = prevChanged.findIndex(c => c.key === imageType);
        if (existingChangeIndex > -1) {
          const updatedChanged = [...prevChanged];
          updatedChanged[existingChangeIndex] = { key: imageType, val: finalUpdatedImagesString };
          return updatedChanged;
        } else {
          return [...prevChanged, { key: imageType, val: finalUpdatedImagesString }];
        }
      });

      return { ...prevStaged, interiorImages: finalUpdatedImagesString };
    });

    if (!editing) {
      setEditing(true);
    }
  }, [editing]);

  // Handle adding a new Imprint
  const handleAddNewImprint = async (inputValue) => {
      if (!inputValue || !currentpublisher?.id) {
          toaster.warning('Cannot add imprint without a value or publisher ID.');
          return null; // Indicate failure or inability to proceed
      }
      try {
          const result = await createImprint({
              variables: {
                  publisherId: String(currentpublisher.id),
                  imprint: inputValue, // Pass the input value directly
              }
          });
          const newImprintId = result.data?.createImprint?.message; // Assuming mutation returns ID in message as per schema_original
          const newImprint = { id: parseInt(newImprintId), value: inputValue, __typename: "PublisherImprintType" };

          // Update global state immediately
          const updatedImprints = [...(publisherGlobalData?.publisherimprints || []), newImprint];
          dispatch(setpublisherGlobalData({ ...publisherGlobalData, publisherimprints: updatedImprints }));
          toaster.success(`Imprint "${inputValue}" added successfully.`);

          // Return the new imprint object so ForeignKeyDropDownInput can select it
          return newImprint;
      } catch (err) {
          toaster.danger(`Error adding imprint: ${err.message}`);
          return null; // Indicate failure
      }
  };

  // Handle adding a new Series
  const handleAddNewSeries = async (inputValue) => {
      if (!inputValue || !currentpublisher?.id) {
          toaster.warning('Cannot add series without a value or publisher ID.');
          return null;
      }
      try {
          const result = await createSeries({
              variables: {
                  publisherId: String(currentpublisher.id),
                  series: inputValue, // Pass the input value directly
              }
          });
           console.log('[Product.js] Create Series result:', result);
          const newSeriesId = result.data?.createSeries?.message; // Assuming mutation returns ID in message as per schema_original
          const newSeries = { id: parseInt(newSeriesId), value: inputValue, __typename: "ProductSeriesType" };

          // Update global state immediately
          const updatedSeries = [...(publisherGlobalData?.productseriess || []), newSeries];
          dispatch(setpublisherGlobalData({ ...publisherGlobalData, productseriess: updatedSeries }));
          toaster.success(`Series "${inputValue}" added successfully.`);

          // Return the new series object
          return newSeries;
      } catch (err) {
          toaster.danger(`Error adding series: ${err.message}`);
          return null;
      }
  };

  if (loading) {
    return <Pane display="flex" alignItems="center" justifyContent="center" height={400}><Spinner /></Pane>;
  }

  if (!product || !productWork || !selectedProduct) {
    return <Pane padding={majorScale(2)}><EmptyState title="Product Not Found" description="The requested product data could not be loaded or is incomplete." icon={<InfoSignIcon color="info" />} iconBgColor="blue100" /></Pane>;
  }

  console.log('[Product.js] Rendering Product component UI (New Layout) with state:', { productId: selectedProduct?.id, workId: productWork?.id, relatedCount: relatedProducts.length });
  // Add log to check publisherGlobalData contents before render
  console.log('[Product.js] Checking publisherGlobalData before render:', {
    imprints: publisherGlobalData?.publisherimprints,
    series: publisherGlobalData?.productseriess
  });

  return (
    <Pane padding={majorScale(3)}>
      <LeavePagePrompt when={editing && changed.length > 0} />

      <Breadcrumbs
        sourceUrl="/products"
        pageLabel="PRODUCT"
        curPage={productWork?.title || "Details"}
      />

      <Pane
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        marginBottom={majorScale(3)}
        paddingBottom={majorScale(2)}
        borderBottom="muted"
      >
        <Pane flex={1} marginRight={majorScale(3)}>
          {editing ? (
            <TextInputFieldInput
              el="productWork.title"
              formLabel="Title"
              value={staged?.productWork?.title || ''}
              handleChange={handleUpdateStaged}
              editing={true}
              width={400}
              opts={{ labelSize: 600, marginBottom: majorScale(1), mandatory: true }}
            />
          ) : (
            <>
              <Pane display="flex" alignItems="center" marginBottom={majorScale(1)}>
                <Heading is="h1" size={800}>
                  {productWork?.title}
                  {selectedProduct.locked && (
                    <Tooltip content={`Recently edited by ${selectedProduct.lastEditor?.email || 'another user'}`}>
                      <LockIcon marginLeft={majorScale(1)} color="muted" />
                    </Tooltip>
                  )}
                </Heading>
                <Pane display="flex" marginLeft="auto" alignItems="center">
                  <Link to={`/orders?productWorkId=${productWork?.id}`} style={{ textDecoration: 'none', color: '#8B1A1A', marginRight: 16, fontSize: 14 }}>
                    Orders({productCounts.ordersCount || 0})
                  </Link>
                  <Link to={`/expenses?productWorkId=${productWork?.id}`} style={{ textDecoration: 'none', color: '#8B1A1A', marginRight: 16, fontSize: 14 }}>
                    Expenses({productCounts.expensesCount || 0})
                  </Link>
                  <Link to={`/royalties?productWorkId=${productWork?.id}`} style={{ textDecoration: 'none', color: '#8B1A1A', fontSize: 14 }}>
                    Royalties({productCounts.royaltiesCount || 0})
                  </Link>
                </Pane>
              </Pane>
            </>
          )}
          <Pane display="flex" alignItems="center" marginBottom={minorScale(2)}>
            {editing ? (
              <>
                {/* Log props passed to Imprint dropdown */}
                {console.log('[Product.js] Imprint Dropdown Props:', {
                  value: staged?.productWork?.imprint,
                  values: publisherGlobalData?.publisherimprints || []
                })}
                <ForeignKeyDropDownInput
                  el="imprint"
                  formLabel="Imprint"
                  value={staged?.productWork?.imprint}
                  handleChange={handleUpdateStaged}
                  editing={true}
                  values={publisherGlobalData?.publisherimprints || []}
                  handleAddNew={handleAddNewImprint}
                  opts={{ marginRight: 16, short: true, preserveObject: true, valueKey: 'id', labelKey: 'value', new: true }}
                  width={200}
                />
                {/* Log props passed to Series dropdown */}
                {console.log('[Product.js] Series Dropdown Props:', {
                  value: staged?.productWork?.series,
                  values: publisherGlobalData?.productseriess || []
                })}
                <ForeignKeyDropDownInput
                  el="series"
                  formLabel="Series"
                  value={staged?.productWork?.series}
                  handleChange={handleUpdateStaged}
                  editing={true}
                  values={publisherGlobalData?.productseriess || []}
                  handleAddNew={handleAddNewSeries}
                  opts={{ marginRight: 16, short: true, preserveObject: true, valueKey: 'id', labelKey: 'value', new: true }}
                  width={200}
                />
              </>
            ) : (
              <>
                <Text size={400} marginRight={majorScale(1)}><Strong>Imprint:</Strong> {productWork?.imprint?.value || 'N/A'}</Text>
                <Text size={400}><Strong>Series:</Strong> {productWork?.series?.value || 'N/A'}</Text>
              </>
            )}
          </Pane>
          {editing ? (
            <TextareaFieldInput
              el="productWork.description"
              formLabel="Description"
              value={staged?.productWork?.description || ''}
              handleChange={handleUpdateStaged}
              editing={true}
              width="100%"
              opts={{ rows: 3 }}
            />
          ) : (
            productWork?.description && (
              <Paragraph color="muted">
                {productWork?.description}
              </Paragraph>
            )
          )}
        </Pane>

        <Pane>
          <SaveButtons
            itemType="product"
            itemId={selectedProduct?.id}
            setEditing={setEditing}
            refetch={refetch}
            resetForm={resetForm}
            locked={selectedProduct?.locked}
            lastEditor={selectedProduct?.lastEditor}
            onSave={handleSave}
            handleUpdate={handleSave}
            handleDelete={handleDelete}
            onCancel={() => { setEditing(false); resetForm(); }}
            canDelete={canDelete}
            editing={editing}
            saveLoading={updateLoading}
            deleteLoading={deleteLoading}
            changed={changed.length > 0 || stagedCreators.length > 0 || deletedCreators.length > 0}
          />
        </Pane>
      </Pane>

      <Pane marginBottom={majorScale(3)}>
        <Pane display="flex" justifyContent="space-between" alignItems="center" marginBottom={majorScale(1)}>
            <Heading size={500}>Formats / Editions</Heading>
            <Button
              iconBefore={PlusIcon}
              onClick={() => setShowAddFormat(true)}
              appearance="minimal"
            >
              Add New Format
            </Button>
        </Pane>
        <Pane display="flex" gap={majorScale(2)} overflowX="auto" paddingBottom={majorScale(1)}>
          {relatedProducts.length > 0 ? (
            relatedProducts.map(p => (
              <Pane key={p.id} flexShrink={0} width={240}>
                  <FormatListItem
                    product={p}
                    isSelected={p.id === selectedProduct?.id}
                    onSelect={handleSelectProduct}
                  />
              </Pane>
            ))
          ) : (
            <Text>No formats found for this work.</Text>
          )}
        </Pane>
      </Pane>

      <Pane>
          <Heading size={500} marginBottom={majorScale(1)}>
              Details for: {selectedProduct.format?.description || 'Selected Format'} (ISBN: {selectedProduct.isbn || 'N/A'})
          </Heading>
          <ProductForm
            product={staged}
            productWork={productWork}
            editing={editing}
            onUpdate={handleUpdateStaged}
            currentCreators={currentCreators}
            stagedCreators={stagedCreators}
            deletedCreators={deletedCreators}
            onAttachCreator={handleAttachOrUpdateCreator}
            onRemoveCreator={handleRemoveCreatorStaged}
            publisherGlobalData={publisherGlobalData}
            editMode={"edit"}
            creators={selectedProduct?.productcreatorSet || []}
            canDelete={canDelete}
            handleDelete={handleDelete}
            handleImageUpload={handleImageUpload}
            handleRemoveInteriorImage={handleRemoveInteriorImage}
          />
      </Pane>

      {productWork && (
        <AddFormatModal
          isShown={showAddFormat}
          onClose={() => setShowAddFormat(false)}
          productWork={productWork}
          publisherId={currentpublisher?.id}
          onSuccess={() => {
            setShowAddFormat(false);
            refetch();
          }}
          selectedProduct={selectedProduct}
        />
      )}
    </Pane>
  );
}

function AddFormatModal({ isShown, onClose, productWork, publisherId, onSuccess, selectedProduct }) {
  const onixGlobalData = useSelector((state) => state.onixGlobalData.value);

  const [formData, setFormData] = useState({
    subtitle: '',
    edition: '',
    volume: '',
    format: null,
    isbn: '',
    retailPrice: '',
    inventoryQuantity: '',
    cartonQty: '',
    shipDate: null,
    publicationDate: null,
    weight: '',
    width: '',
    height: '',
    thickness: '',
    pageCount: '',
    adminNotes: '',
    status: null,
    formatDetail: null,
    creatorIds: '[]',
  });
  const [formErrors, setFormErrors] = useState({});
  const [sourceCreators, setSourceCreators] = useState([]);

  const [createProduct, { loading, error }] = useMutation(CREATE_PRODUCT_MUTATION);

  // Parse JSON dimension fields if they exist
  const parseJsonDimension = (dimValue) => {
    try {
      // Handle null/undefined
      if (dimValue == null) {
        return '';
      }

      // If it's already a string, try to parse it as JSON
      if (typeof dimValue === 'string') {
        // Check if it's a JSON string
        if (dimValue.startsWith('{') || dimValue.startsWith('\"')) {
          try {
            // Handle double-encoded JSON strings
            const parsedValue = dimValue.startsWith('\"') ?
              JSON.parse(JSON.parse(dimValue)) :
              JSON.parse(dimValue);

            // Return just the value portion
            return parsedValue?.value || '';
          } catch (jsonErr) {
            // If JSON parsing fails, treat as a direct value
            console.log(`JSON parse error for ${dimValue}: ${jsonErr.message}`);
            return dimValue;
          }
        } else {
          // It's a direct string value
          return dimValue;
        }
      }

      // If it's a number, convert to string
      if (typeof dimValue === 'number') {
        return dimValue.toString();
      }

      // If it's already an object with the right structure
      if (typeof dimValue === 'object' && dimValue !== null) {
        return dimValue.value || '';
      }

      // Fallback for any other case
      return '';
    } catch (err) {
      console.error(`Error parsing dimension: ${err.message}`, dimValue);
      return ''; // Return empty value if parsing fails
    }
  };

  // Reset form when modal is opened
  useEffect(() => {
    if (isShown) {
      setFormData({
        subtitle: '',
        edition: '',
        volume: '',
        format: null,
        isbn: '',
        retailPrice: '',
        inventoryQuantity: '',
        cartonQty: '',
        shipDate: null,
        publicationDate: null,
        weight: '',
        width: '',
        height: '',
        thickness: '',
        pageCount: '',
        adminNotes: '',
        status: null,
        formatDetail: null,
        creatorIds: '[]',
      });
      setSourceCreators([]);
      setFormErrors({});
    }
  }, [isShown, productWork]);

  // Populate form with existing product data if editing a product
  useEffect(() => {
    if (isShown && selectedProduct) {
      // Parse dimensions from JSON if needed
      const weight = parseJsonDimension(selectedProduct.weight);
      const width = parseJsonDimension(selectedProduct.width);
      const height = parseJsonDimension(selectedProduct.height);
      const thickness = parseJsonDimension(selectedProduct.thickness);

      // Parse dates if they exist
      const shipDate = selectedProduct.shipDate ? new Date(selectedProduct.shipDate) : null;
      const publicationDate = selectedProduct.publicationDate ? new Date(selectedProduct.publicationDate) : null;

      console.log("Setting form data with dimensions:", {
        weight, width, height, thickness,
        originalWeight: selectedProduct.weight,
        shipDate,
        publicationDate
      });

      setFormData(prev => ({
        ...prev,
        subtitle: selectedProduct.subtitle || '',
        shipDate: shipDate,
        publicationDate: publicationDate,
        status: selectedProduct.status || null,
        format: selectedProduct.format || null,
        weight: weight,
        width: width,
        height: height,
        thickness: thickness,
        pageCount: selectedProduct.pageCount || '',
        cartonQty: selectedProduct.cartonQty || '',
        inventoryQuantity: selectedProduct.inventoryQuantity || '',
        retailPrice: selectedProduct.retailPrice || '',
        isbn: '',
        adminNotes: selectedProduct.adminNotes || '',
        edition: selectedProduct.edition || '',
        volume: selectedProduct.volume || '',
      }));
      const creators = selectedProduct.productcreatorSet || [];
      setSourceCreators(creators);
    }
  }, [isShown, selectedProduct]);

  const handleChange = (key, value) => {
    const fieldKey = typeof key === 'string' ? key : key.target.name;
    const fieldValue = typeof key === 'string' ? value : key.target.value;

    console.log(`Updating field ${fieldKey} with value:`, fieldValue);

    setFormData(prev => ({ ...prev, [fieldKey]: fieldValue }));
    if (formErrors[fieldKey]) {
      setFormErrors(prev => ({ ...prev, [fieldKey]: null }));
    }
  };

  const handleFormatChange = (obj) => {
    const selectedFormat = obj?.val || null;
    setFormData(prev => ({ ...prev, format: selectedFormat }));
    if (formErrors.format) {
      setFormErrors(prev => ({ ...prev, format: null }));
    }
  };

  const handleDateChange = (key, date) => {
    console.log(`Setting date for ${key}:`, date);
    setFormData(prev => ({ ...prev, [key]: date }));
    if (formErrors[key]) {
      setFormErrors(prev => ({ ...prev, [key]: null }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.format) errors.format = "Format is required.";
    if (!formData.isbn) errors.isbn = "ISBN/UID is required.";
    if (!formData.retailPrice || isNaN(parseFloat(formData.retailPrice))) errors.retailPrice = "Valid Retail Price is required.";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    // Format creatorIds from sourceCreators state
    let creatorIdsJson = '[]';
    if (sourceCreators && sourceCreators.length > 0) {
      const creatorData = sourceCreators.map(pc => ({
        creatorId: parseInt(pc.creator.id), // Ensure integer
        onixCodeId: parseInt(pc.onixContributorCode.id) // Ensure integer
      }));
      creatorIdsJson = JSON.stringify(creatorData);
    }

    try {
      // Parse values to the correct types
      const parsedRetailPrice = parseFloat(formData.retailPrice);
      const parsedInventory = parseInt(formData.inventoryQuantity);
      const parsedCartonQty = parseInt(formData.cartonQty);

      // Directly parse dimension values to Floats
      const parsedWeight = formData.weight ? parseFloat(formData.weight) : null;
      const parsedWidth = formData.width ? parseFloat(formData.width) : null;
      const parsedHeight = formData.height ? parseFloat(formData.height) : null;
      const parsedThickness = formData.thickness ? parseFloat(formData.thickness) : null;

      const parsedPageCount = parseInt(formData.pageCount);

      // Format dates as strings (YYYY-MM-DD)
      const formattedShipDate = formData.shipDate ? makeDateString(formData.shipDate) : null;
      const formattedPublicationDate = formData.publicationDate ? makeDateString(formData.publicationDate) : null;

      console.log("Formatted dates:", {
        shipDate: formattedShipDate,
        publicationDate: formattedPublicationDate
      });

      const variables = {
        publisherId: String(publisherId),
        productWorkId: parseInt(productWork.id),
        subtitle: formData.subtitle || null,
        edition: formData.edition || null,
        volume: formData.volume || null,
        formatId: String(formData.format?.id),
        formatDetailId: formData.formatDetail?.id ? String(formData.formatDetail.id) : null,
        isbn: formData.isbn,
        retailPrice: !isNaN(parsedRetailPrice) ? parsedRetailPrice : 0,
        inventory: !isNaN(parsedInventory) ? parsedInventory : 0,
        cartonQty: !isNaN(parsedCartonQty) ? parsedCartonQty : 0,
        shipDate: formattedShipDate,
        publicationDate: formattedPublicationDate,
        // Pass parsed Float values directly
        weight: !isNaN(parsedWeight) ? parsedWeight : null,
        width: !isNaN(parsedWidth) ? parsedWidth : null,
        height: !isNaN(parsedHeight) ? parsedHeight : null,
        thickness: !isNaN(parsedThickness) ? parsedThickness : null,
        pageCount: !isNaN(parsedPageCount) ? parsedPageCount : 0,
        adminNotes: formData.adminNotes || null,
        status: formData.status?.id ? parseInt(formData.status.id) : null,
        creatorIds: creatorIdsJson,
      };

      // Remove null/undefined/empty values
      Object.keys(variables).forEach(key => (variables[key] === null || variables[key] === undefined || variables[key] === '') && delete variables[key]);

      // Ensure subtitle, edition, volume are handled properly
      if (!variables.subtitle) variables.subtitle = null;
      if (!variables.edition) variables.edition = null;
      if (!variables.volume) variables.volume = null;

      console.log("Submitting variables:", variables);

      const result = await createProduct({ variables });

      if (result.data?.createProduct?.product) {
        toaster.success("New format added successfully!");
        onSuccess();
      } else {
        const gqlError = result.errors?.[0];
        const errorMessage = gqlError?.message || 'Unknown error creating format.';
        toaster.danger(`Failed to add new format: ${errorMessage}`);
      }
    } catch (err) {
      console.error("Error submitting form:", err);
      toaster.danger(`Error adding format: ${err.message}`);
    }
  };

  // Helper function to format dates for display
  const formatDate = (date) => {
    if (!date) return "";
    try {
      const d = new Date(date);
      return d.toISOString().split('T')[0]; // YYYY-MM-DD format
    } catch (e) {
      console.error("Error formatting date:", e);
      return date;
    }
  };

  return (
    <Dialog
      isShown={isShown}
      title={`Add New Format for "${productWork.title}"`}
      onCloseComplete={onClose}
      confirmLabel={loading ? "Adding..." : "Add Format"}
      isConfirmLoading={loading}
      onConfirm={handleSubmit}
      width={majorScale(100)}
      shouldCloseOnOverlayClick={false}
      preventBodyScrolling
    >
      <Pane padding={majorScale(2)} maxHeight="70vh" overflowY="auto">
        {error && (
            <>
                <Paragraph color="danger" marginBottom={majorScale(2)}>Mutation Error: {error.message}</Paragraph>
            </>
        )}

        <Pane display="flex" marginBottom={majorScale(2)} alignItems="flex-start">
          <Pane flex={1} marginRight={majorScale(2)}>
            <ForeignKeyDropDownInput
              el="format"
              formLabel="Format"
              values={onixGlobalData.onixproductforms || []}
              value={formData.format}
              handleChange={handleFormatChange}
              editing={true}
              isInvalid={!!formErrors.format}
              validationMessage={formErrors.format}
              opts={{
                mandatory: true,
                short: true,
                valueKey: 'id',
                labelKey: 'description'
              }}
            />
          </Pane>
          {formData.format && formData.format.description === 'Hardcover' && (
            <Pane flex={1} marginRight={majorScale(2)}>
              <ForeignKeyDropDownInput
                el="formatDetail"
                formLabel="Format Detail"
                values={onixGlobalData.onixproductformdetails || []}
                value={formData.formatDetail}
                handleChange={(obj) => setFormData(prev => ({ ...prev, formatDetail: obj?.val || null }))}
                editing={true}
                opts={{
                  short: true,
                  valueKey: 'id',
                  labelKey: 'description',
                  marginRight: 16
                }}
              />
            </Pane>
          )}
          <Pane flex={1} marginRight={majorScale(2)}>
            <TextInputField
              label="ISBN / UID"
              required
              value={formData.isbn}
              onChange={e => handleChange('isbn', e.target.value)}
              isInvalid={!!formErrors.isbn}
              validationMessage={formErrors.isbn}
              width="100%"
            />
          </Pane>
          <Pane flex={2}>
            <TextInputField
              label="Subtitle"
              value={formData.subtitle}
              onChange={e => handleChange('subtitle', e.target.value)}
              width="100%"
            />
          </Pane>
        </Pane>

        <Pane display="flex" marginBottom={majorScale(2)} alignItems="flex-start">
          <Pane flex={1} marginRight={majorScale(2)}>
            <TextInputField
              label="Retail Price"
              type="number"
              step="0.01"
              required
              value={formData.retailPrice}
              onChange={e => handleChange('retailPrice', e.target.value)}
              isInvalid={!!formErrors.retailPrice}
              validationMessage={formErrors.retailPrice}
              width="100%"
            />
          </Pane>
          <Pane flex={1} marginRight={majorScale(2)}>
            <TextInputField
              label="Inventory Qty"
              type="number"
              step="1"
              value={formData.inventoryQuantity}
              onChange={e => handleChange('inventoryQuantity', e.target.value)}
              width="100%"
            />
          </Pane>
          <Pane flex={1}>
            <TextInputField
              label="Carton Qty"
              type="number"
              step="1"
              value={formData.cartonQty}
              onChange={e => handleChange('cartonQty', e.target.value)}
              width="100%"
            />
          </Pane>
        </Pane>

        <Pane display="flex" marginBottom={majorScale(2)} alignItems="flex-start">
          <Pane flex={1} marginRight={majorScale(2)}>
            <TextInputField
              label="Edition"
              value={formData.edition}
              onChange={e => handleChange('edition', e.target.value)}
              width="100%"
            />
          </Pane>
          <Pane flex={1} marginRight={majorScale(2)}>
            <TextInputField
              label="Volume"
              value={formData.volume}
              onChange={e => handleChange('volume', e.target.value)}
              width="100%"
            />
          </Pane>
          <Pane flex={1}>
            <TextInputField
              label="Page Count"
              type="number"
              step="1"
              value={formData.pageCount}
              onChange={e => handleChange('pageCount', e.target.value)}
              width="100%"
            />
          </Pane>
        </Pane>

        <Pane display="flex" marginBottom={majorScale(2)} alignItems="flex-start">
          <Pane flex={1} marginRight={majorScale(2)}>
            <TextInputField
              label="Weight (lbs)"
              type="number"
              step="0.01"
              value={formData.weight}
              onChange={e => handleChange('weight', e.target.value)}
              width="100%"
            />
          </Pane>
          <Pane flex={1} marginRight={majorScale(2)}>
            <TextInputField
              label="Width (in)"
              type="number"
              step="0.01"
              value={formData.width}
              onChange={e => handleChange('width', e.target.value)}
              width="100%"
            />
          </Pane>
          <Pane flex={1} marginRight={majorScale(2)}>
            <TextInputField
              label="Height (in)"
              type="number"
              step="0.01"
              value={formData.height}
              onChange={e => handleChange('height', e.target.value)}
              width="100%"
            />
          </Pane>
          <Pane flex={1}>
            <TextInputField
              label="Thickness (in)"
              type="number"
              step="0.01"
              value={formData.thickness}
              onChange={e => handleChange('thickness', e.target.value)}
              width="100%"
            />
          </Pane>
        </Pane>

        <Pane display="flex" marginBottom={majorScale(2)} alignItems="flex-start">
          <Pane flex={1} marginRight={majorScale(2)}>
            <Pane>
              <Paragraph size={300} color="#667b7f" marginBottom={minorScale(1)}>Ship Date</Paragraph>
              <DatePicker
                value={formData.shipDate}
                onChange={(date) => handleDateChange('shipDate', date)}
              />
            </Pane>
          </Pane>
          <Pane flex={1}>
            <Pane>
              <Paragraph size={300} color="#667b7f" marginBottom={minorScale(1)}>Publication Date</Paragraph>
              <DatePicker
                value={formData.publicationDate}
                onChange={(date) => handleDateChange('publicationDate', date)}
              />
            </Pane>
          </Pane>
        </Pane>

        <Pane display="flex" marginBottom={majorScale(2)} alignItems="flex-start">
          <Pane flex={1} marginRight={majorScale(2)}>
            <ForeignKeyDropDownInput
              el="status"
              formLabel="Status"
              values={onixGlobalData.onixproductavailabilitys || []}
              value={formData.status}
              handleChange={(obj) => setFormData(prev => ({ ...prev, status: obj?.val || null }))}
              editing={true}
              opts={{
                short: true,
                valueKey: 'id',
                labelKey: 'description'
              }}
            />
          </Pane>
          <Pane flex={2}>
          </Pane>
        </Pane>
      </Pane>
    </Dialog>
  );
}

function getImageFilename(url) {
  if (url) {
    return url.split("/").pop();
  } else {
    return "";
  }
}

function checkInteriorImages(arrStr) {
  if (arrStr && arrStr !== "") {
    try {
      let jsonobj = JSON.parse(arrStr);
      return Array.isArray(jsonobj);
    } catch (e) {
      return false;
    }
  } else {
    return false;
  }
}

export default Product;
